import { ApiResponse } from '@/types/api.types';
import { Category } from '@/types/models/tournament';
import { apiService } from './api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { logger } from '@/utils/logger';

export class CategoryService {
    static async getAll(): Promise<Category[]> {
        try {            
            const { data: response } = await apiService.get<ApiResponse<Category[]>>(
                API_ENDPOINTS.CATEGORIES.BASE
            );
            
            return response.data;
        } catch (error) {
            logger.error('Failed to fetch categories:', error);
            throw error;
        }
    }

    static async getById(id: number): Promise<Category> {
        try {
            const { data: response } = await apiService.get<ApiResponse<Category>>(
                API_ENDPOINTS.CATEGORIES.GET(id)
            );
            return response.data;
        } catch (error) {
            logger.error(`Failed to fetch category ${id}:`, error);
            throw error;
        }
    }

    static async create(data: Partial<Category>): Promise<Category> {
        try {
            const { data: response } = await apiService.post<ApiResponse<Category>>(
                API_ENDPOINTS.CATEGORIES.CREATE,
                data
            );
            return response.data;
        } catch (error) {
            logger.error('Failed to create category:', error);
            throw error;
        }
    }

    static async update(id: number, data: Partial<Category>): Promise<Category> {
        try {
            const { data: response } = await apiService.put<ApiResponse<Category>>(
                API_ENDPOINTS.CATEGORIES.UPDATE(id),
                data
            );
            return response.data;
        } catch (error) {
            logger.error(`Failed to update category ${id}:`, error);
            throw error;
        }
    }

    static async delete(id: number): Promise<void> {
        try {
            await apiService.delete(API_ENDPOINTS.CATEGORIES.DELETE(id));
        } catch (error) {
            logger.error(`Failed to delete category ${id}:`, error);
            throw error;
        }
    }

    static async checkExists(params: {
        weightFrom: number;
        weightTo: number;
        ageFrom: number;
        ageTo: number;
        gender: 'male' | 'female' | 'mixed';
        beltLevels?: string[];
    }): Promise<ApiResponse<{ exists: boolean; data?: Category }>> {
        try {
            const queryParams = new URLSearchParams({
                weightFrom: params.weightFrom.toString(),
                weightTo: params.weightTo.toString(),
                ageFrom: params.ageFrom.toString(),
                ageTo: params.ageTo.toString(),
                gender: params.gender
            });
            
            const { data: response } = await apiService.get<ApiResponse<{ exists: boolean; data?: Category }>>(
                `/categories/check?${queryParams.toString()}`
            );
            return response;
        } catch (error) {
            logger.error('Failed to check category:', error);
            throw error;
        }
    }
}

export default CategoryService;
